'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { useLayoutContent } from '@/components/providers/ContentProvider';
import { addUTMs } from '@/lib/utils';

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const isPro = pathname === '/organizadores';
  const isAmbassador = pathname === '/embajadores';
  const isFaq = pathname === '/preguntas-frecuentes';

  const { global: g, nav } = useLayoutContent();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const faqLinks = [
    { label: 'Club', href: '/' },
    { label: 'Organizadores', href: '/organizadores' },
  ];

  const links = isFaq
    ? []
    : isAmbassador
      ? nav.ambassadors
      : isPro
        ? nav.pro
        : nav.club;
  /** Club has a light hero so we use dark nav from the start; Pro and Ambassador use light nav only when scrolled */
  const useLightNavStyle = scrolled || (!isPro && !isAmbassador);

  const ctaText = isAmbassador
    ? nav.ambassadorCtaText
    : isPro
      ? nav.proCtaText
      : nav.clubCtaText;

  const ctaHref = isAmbassador
    ? '#aplicar'
    : isPro
      ? addUTMs(g.dashboardUrl, 'pro_nav')
      : addUTMs(g.appUrl, 'club_nav');

  return (
    <>
      <nav
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
          scrolled
            ? 'bg-zibarit-bone/95 backdrop-blur-md shadow-sm'
            : useLightNavStyle
              ? 'bg-zibarit-bone/80 backdrop-blur-sm'
              : 'bg-transparent',
        )}
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 box-border">
          <div className="flex items-center justify-between gap-3 h-16 md:h-20 min-w-0">
            {/* Logo: Club → /, Pro → /organizadores, Ambassador → /embajadores */}
            <Link href={isAmbassador ? '/embajadores' : isPro ? '/organizadores' : '/'} className="flex items-center gap-1 min-w-0 flex-shrink">
              {isPro ? (
                <span className="relative block w-[220px] h-12 sm:w-[280px] sm:h-14 md:w-[340px] md:h-[90px] flex-shrink-0">
                  <Image
                    src="/logos/zibarit-pro-dark.svg"
                    alt="Zibarit Pro"
                    fill
                    sizes="(max-width: 640px) 220px, (max-width: 768px) 280px, 340px"
                    className={cn(
                      'object-contain object-left transition-all duration-500',
                      !useLightNavStyle && 'brightness-0 invert',
                    )}
                  />
                </span>
              ) : (
                <span className="relative block w-[220px] h-12 sm:w-[280px] sm:h-14 md:w-[340px] md:h-[90px] flex-shrink-0">
                  <Image
                    src="/logos/zibarit-club-dark.svg"
                    alt={isAmbassador ? 'Zibarit Embajadores' : 'Zibarit Club'}
                    fill
                    sizes="(max-width: 640px) 220px, (max-width: 768px) 280px, 340px"
                    className={cn(
                      'object-contain object-left transition-all duration-500',
                      !useLightNavStyle && 'brightness-0 invert',
                    )}
                  />
                </span>
              )}
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8">
              {(isFaq ? faqLinks : links).map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  data-ph-capture
                  className={cn(
                    'text-body text-sm transition-colors duration-300 hover:opacity-70',
                    useLightNavStyle ? 'text-zibarit-negro' : 'text-zibarit-bone',
                  )}
                >
                  {link.label}
                </Link>
              ))}
              {!isPro && !isAmbassador && !isFaq && (
                <Link
                  href="/organizadores"
                  className={cn(
                    'text-body text-sm transition-colors duration-300 hover:opacity-70',
                    useLightNavStyle ? 'text-zibarit-verde-oscuro' : 'text-zibarit-marron-secundario',
                  )}
                >
                  Organizadores
                </Link>
              )}
              {!isFaq && (
                <a
                  href={ctaHref}
                  target={isAmbassador ? undefined : '_blank'}
                  rel={isAmbassador ? undefined : 'noopener noreferrer'}
                  data-ph-capture
                  className={cn(
                    'inline-flex items-center justify-center px-5 py-2 rounded-full text-sm font-lato font-bold uppercase tracking-wider transition-all duration-300',
                    isPro
                      ? useLightNavStyle
                        ? 'bg-zibarit-negro text-zibarit-bone hover:bg-zibarit-gris-oscuro'
                        : 'bg-zibarit-bone text-zibarit-negro hover:bg-white'
                      : useLightNavStyle
                      ? 'bg-zibarit-verde-oscuro text-zibarit-bone hover:bg-zibarit-gris-oscuro'
                      : 'bg-zibarit-bone text-zibarit-negro hover:bg-white',
                  )}
                >
                  {ctaText}
                </a>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 z-50 flex-shrink-0"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              <div className="w-6 h-5 relative flex flex-col justify-between">
                <motion.span
                className={cn(
                  'w-full h-0.5 block transition-colors',
                  useLightNavStyle && !mobileOpen ? 'bg-zibarit-negro' : 'bg-zibarit-bone',
                )}
                  animate={mobileOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.span
                className={cn(
                  'w-full h-0.5 block transition-colors',
                  useLightNavStyle && !mobileOpen ? 'bg-zibarit-negro' : 'bg-zibarit-bone',
                )}
                  animate={mobileOpen ? { opacity: 0 } : { opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.span
                className={cn(
                  'w-full h-0.5 block transition-colors',
                  useLightNavStyle && !mobileOpen ? 'bg-zibarit-negro' : 'bg-zibarit-bone',
                )}
                  animate={mobileOpen ? { rotate: -45, y: -10 } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-zibarit-negro flex flex-col items-center justify-center gap-8"
          >
            {(isFaq ? faqLinks : links).map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="text-title text-3xl text-zibarit-bone hover:text-zibarit-verde-oscuro transition-colors"
              >
                {link.label}
              </Link>
            ))}
            {!isPro && !isAmbassador && !isFaq && (
              <Link
                href="/organizadores"
                onClick={() => setMobileOpen(false)}
                className="text-title text-3xl text-zibarit-verde-oscuro hover:text-zibarit-bone transition-colors"
              >
                Organizadores
              </Link>
            )}
            {!isFaq && (
              <a
                href={ctaHref}
                target={isAmbassador ? undefined : '_blank'}
                rel={isAmbassador ? undefined : 'noopener noreferrer'}
                onClick={() => setMobileOpen(false)}
                data-ph-capture
                className={cn(
                  'mt-4 inline-flex items-center justify-center px-8 py-3 rounded-full font-lato font-bold uppercase tracking-wider',
                  isPro
                    ? 'bg-zibarit-bone text-zibarit-negro'
                    : 'bg-zibarit-verde-oscuro text-zibarit-bone',
                )}
              >
                {ctaText}
              </a>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
